import { useEffect, useMemo, useState } from "react";
import type { CaseRecord, RecordFact } from "../types";
import { api } from "../api";
import { displayFactTitle, formatFactDate } from "../lib/factMeta";
import { formatCents } from "../lib/costsMath";
import {
  buildExpenseRows,
  computeStatement,
  computeVendorFlags,
  isEstimated,
  isIncluded,
  rowNote,
  buildStatementDoc,
  type ExpenseRow,
  type RowOverride,
} from "../lib/expenseStatement";
import {
  exportStatementDocx,
  exportStatementPdf,
} from "../lib/exportExpenseStatement";
import QuoteContextPanel from "./ui/QuoteContextPanel";
import { AiMark, VerifiedMark } from "./ui/ProvenanceMark";

// Expense statement (Round 8): machine completeness, human judgment. The
// table lists every verified cost amount (deterministic); the user curates
// include/exclude (persisted by content key); the headline total is the
// exact integer-cent sum of included rows; the export carries the proof.
// One screen — a curation table, not a wizard.
//
// Round 9 adds per-row honesty controls: a user note and an "estimated"
// flag (both persisted and exported), and a same-vendor overlap prompt for
// rows that may describe the same expense at different times.
export default function ExpenseStatement({
  caseId,
  caseName,
  records,
  onClose,
  onNavigateToRecord,
}: {
  caseId: string;
  caseName: string;
  records: CaseRecord[];
  onClose: () => void;
  onNavigateToRecord?: (recordId: string) => void;
}) {
  const [facts, setFacts] = useState<RecordFact[]>([]);
  const [loading, setLoading] = useState(true);
  const [overrides, setOverrides] = useState<Map<string, RowOverride>>(new Map());
  const [showOthers, setShowOthers] = useState(false);
  const [quoteView, setQuoteView] = useState<RecordFact | null>(null);
  const [exporting, setExporting] = useState(false);
  // "Keep all" dismissals for the same-vendor prompt (session-local: the
  // prompt is advisory, and re-surfacing on a fresh open is the safe side).
  const [dismissedVendor, setDismissedVendor] = useState<Set<string>>(new Set());
  // Inline note editing: the row key being edited + the draft text.
  const [noteEditKey, setNoteEditKey] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState("");

  useEffect(() => {
    let live = true;
    Promise.all([api.getCaseFacts(caseId), api.getExpenseOverrides(caseId)])
      .then(([f, o]) => {
        if (!live) return;
        setFacts(f.facts);
        setOverrides(
          new Map(
            o.overrides.map((x) => [
              x.fact_key,
              {
                included: x.included,
                note: x.note ?? undefined,
                estimated: x.estimated,
              },
            ]),
          ),
        );
      })
      .catch(() => {
        /* empty pool renders the empty state */
      })
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [caseId]);

  const recordById = useMemo(
    () => new Map(records.map((r) => [r.id, r])),
    [records],
  );
  const pool = useMemo(
    () => buildExpenseRows(facts, recordById),
    [facts, recordById],
  );
  const statement = useMemo(
    () => computeStatement(pool, overrides),
    [pool, overrides],
  );
  const includedKeys = useMemo(
    () => new Set(statement.included.map((r) => r.key)),
    [statement],
  );
  const vendorFlags = useMemo(
    () => computeVendorFlags(statement.included, recordById, dismissedVendor),
    [statement, recordById, dismissedVendor],
  );
  const anyEstimated = statement.included.some((r) => isEstimated(r, overrides));

  async function setRow(row: ExpenseRow, patch: RowOverride) {
    // Optimistic; revert on failure. The server stores the FULL row state.
    const prevState = overrides.get(row.key);
    const next: RowOverride = {
      included: isIncluded(row, overrides),
      note: rowNote(row, overrides) || undefined,
      estimated: isEstimated(row, overrides),
      ...patch,
    };
    setOverrides((prev) => new Map(prev).set(row.key, next));
    try {
      await api.setExpenseOverride(caseId, row.key, {
        included: next.included ?? row.seeded,
        note: next.note ?? null,
        estimated: next.estimated ?? false,
      });
    } catch {
      setOverrides((prev) => {
        const reverted = new Map(prev);
        if (prevState) reverted.set(row.key, prevState);
        else reverted.delete(row.key);
        return reverted;
      });
    }
  }

  const rowByKey = useMemo(() => {
    const m = new Map<string, ExpenseRow>();
    for (const r of [...pool.seeded, ...pool.other]) m.set(r.key, r);
    return m;
  }, [pool]);

  function saveNoteDraft() {
    if (noteEditKey == null) return;
    const row = rowByKey.get(noteEditKey);
    if (row) void setRow(row, { note: noteDraft.trim() || undefined });
    setNoteEditKey(null);
  }

  async function handleExport(format: "pdf" | "docx") {
    if (statement.totalCents == null) return; // fold conflict unresolved
    const flagged = statement.duplicateFlags.length + vendorFlags.length;
    if (flagged > 0) {
      const ok = window.confirm(
        `${flagged} possible duplicate/overlapping amount${flagged === 1 ? " is" : "s are"} still flagged. Export anyway?`,
      );
      if (!ok) return;
    }
    setExporting(true);
    try {
      const doc = buildStatementDoc(
        caseName,
        statement.included,
        statement.totalCents,
        recordById,
        new Date().toISOString(),
        overrides,
      );
      if (format === "pdf") await exportStatementPdf(doc);
      else await exportStatementDocx(doc);
    } finally {
      setExporting(false);
    }
  }

  const renderRow = (row: ExpenseRow) => {
    const rec = recordById.get(row.fact.record_id);
    const included = isIncluded(row, overrides);
    const note = rowNote(row, overrides);
    const estimated = isEstimated(row, overrides);
    return (
      <tr key={row.key} style={included ? undefined : { opacity: 0.5 }}>
        <td>
          <input
            type="checkbox"
            checked={included}
            onChange={(e) => void setRow(row, { included: e.target.checked })}
            aria-label={included ? "Exclude from statement" : "Include in statement"}
          />
        </td>
        <td>{formatFactDate(row.fact.date)}</td>
        <td>
          {displayFactTitle(row.fact)} <AiMark what="Description" />{" "}
          <button
            type="button"
            className="fact-source-link"
            onClick={() => setQuoteView(row.fact)}
          >
            quote
          </button>
          {row.duplicates.length > 0 && (
            <span className="overview-muted">
              {" "}
              (stated {row.duplicates.length + 1}× in this record)
            </span>
          )}
          <div className="expense-note-line">
            {noteEditKey === row.key ? (
              <input
                type="text"
                className="expense-note-input"
                value={noteDraft}
                maxLength={500}
                placeholder="Note for this row (appears in the export)"
                autoFocus
                onChange={(e) => setNoteDraft(e.target.value)}
                onBlur={saveNoteDraft}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveNoteDraft();
                  if (e.key === "Escape") setNoteEditKey(null);
                }}
              />
            ) : note ? (
              <span className="overview-muted expense-note">
                Note: {note}{" "}
                <button
                  type="button"
                  className="fact-source-link"
                  onClick={() => {
                    setNoteDraft(note);
                    setNoteEditKey(row.key);
                  }}
                >
                  edit
                </button>
              </span>
            ) : (
              <button
                type="button"
                className="fact-source-link expense-note-add"
                onClick={() => {
                  setNoteDraft("");
                  setNoteEditKey(row.key);
                }}
              >
                + note
              </button>
            )}
          </div>
        </td>
        <td className="num">
          {formatCents(row.cents)} <VerifiedMark what="Amount" />
          <label className="expense-est-toggle">
            <input
              type="checkbox"
              checked={estimated}
              onChange={(e) => void setRow(row, { estimated: e.target.checked })}
            />{" "}
            estimated
          </label>
        </td>
        <td>
          <button
            type="button"
            className="fact-source-link"
            onClick={() => onNavigateToRecord?.(row.fact.record_id)}
          >
            {rec?.subject?.trim() || "Record"}
          </button>
        </td>
      </tr>
    );
  };

  return (
    <div className="quote-context-overlay" role="dialog" aria-label="Expense statement">
      <div className="quote-context-panel expense-statement-panel">
        <div className="quote-context-head">
          <div>
            <strong>Expense statement</strong>{" "}
            <span className="overview-muted">{caseName}</span>
          </div>
          <div className="quote-context-actions">
            <button
              type="button"
              className="fact-source-link"
              disabled={exporting || statement.totalCents == null}
              onClick={() => void handleExport("pdf")}
            >
              Export PDF
            </button>
            <button
              type="button"
              className="fact-source-link"
              disabled={exporting || statement.totalCents == null}
              onClick={() => void handleExport("docx")}
            >
              Export Word
            </button>
            <button type="button" className="fact-source-link" onClick={onClose}>
              Close ✕
            </button>
          </div>
        </div>

        {loading && <p className="overview-muted">Loading verified amounts…</p>}

        {!loading && pool.seeded.length === 0 && pool.other.length === 0 && (
          <p className="overview-banner">
            No verified amounts in this case yet — run fact extraction first.
          </p>
        )}

        {statement.foldConflicts.map((fc) => (
          <div className="overview-banner" key={`fold-${fc.total.key}`}>
            A stated total and its components are both included (
            {formatCents(fc.total.cents)} ={" "}
            {fc.components.map((c) => formatCents(c.cents)).join(" + ")}).
            Including both would double-count.{" "}
            <button
              type="button"
              className="fact-source-link"
              onClick={() => {
                for (const c of fc.components) void setRow(c, { included: false });
              }}
            >
              Keep just the total
            </button>{" "}
            ·{" "}
            <button
              type="button"
              className="fact-source-link"
              onClick={() => void setRow(fc.total, { included: false })}
            >
              Keep just the components
            </button>
          </div>
        ))}

        {statement.duplicateFlags.map((d, i) => (
          <div className="overview-banner" key={`dup-${i}`}>
            These rows are both {d.amountLabel} — same expense?{" "}
            <button
              type="button"
              className="fact-source-link"
              onClick={() => void setRow(d.a, { included: false })}
            >
              Exclude "{displayFactTitle(d.a.fact)}"
            </button>{" "}
            ·{" "}
            <button
              type="button"
              className="fact-source-link"
              onClick={() => void setRow(d.b, { included: false })}
            >
              Exclude "{displayFactTitle(d.b.fact)}"
            </button>
          </div>
        ))}

        {vendorFlags.map((v) => (
          <div className="overview-banner" key={`vendor-${v.key}`}>
            These {v.rows.length} rows share one vendor/agreement ("
            {v.vendorLabel}") and may describe the same expense at different
            times — keep the most recent?{" "}
            <button
              type="button"
              className="fact-source-link"
              onClick={() => {
                for (const r of v.rows)
                  if (!v.latest.includes(r)) void setRow(r, { included: false });
              }}
            >
              Keep most recent
            </button>{" "}
            ·{" "}
            <button
              type="button"
              className="fact-source-link"
              onClick={() =>
                setDismissedVendor((prev) => new Set(prev).add(v.key))
              }
            >
              Keep all
            </button>
          </div>
        ))}

        {!loading && pool.seeded.length > 0 && (
          <div className="table-scroll">
          <table className="fact-table">
            <thead>
              <tr>
                <th />
                <th>Date</th>
                <th>Description</th>
                <th className="num">Amount</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>{pool.seeded.map(renderRow)}</tbody>
            <tfoot>
              <tr>
                <td colSpan={3} className="fact-table-total">
                  Statement total ({includedKeys.size} row
                  {includedKeys.size === 1 ? "" : "s"}
                  {anyEstimated ? ", includes estimated amounts" : ""})
                </td>
                <td className="num fact-table-total">
                  {statement.totalCents != null
                    ? formatCents(statement.totalCents)
                    : "— resolve the total/components prompt above"}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
          </div>
        )}

        {!loading && pool.other.length > 0 && (
          <details
            className="fact-components"
            open={showOthers}
            onToggle={(e) => setShowOthers((e.target as HTMLDetailsElement).open)}
          >
            <summary>
              Add other amounts ({pool.other.length} verified amount
              {pool.other.length === 1 ? "" : "s"} not flagged as costs)
            </summary>
            <div className="table-scroll">
            <table className="fact-table">
              <tbody>{pool.other.map(renderRow)}</tbody>
            </table>
            </div>
          </details>
        )}

        {quoteView && (
          <QuoteContextPanel
            fact={quoteView}
            record={recordById.get(quoteView.record_id)}
            onClose={() => setQuoteView(null)}
            onNavigateToRecord={onNavigateToRecord}
          />
        )}
      </div>
    </div>
  );
}
